import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import { getAutoMaintain, getFirmwareInfo, patchAutoMaintain } from "../../api/config";
import type { Device } from "../../types";

const DAY_OPTS = [
  { value: "-1", label: "Todos los días" },
  { value: "0", label: "Domingo" },
  { value: "1", label: "Lunes" },
  { value: "2", label: "Martes" },
  { value: "3", label: "Miércoles" },
  { value: "4", label: "Jueves" },
  { value: "5", label: "Viernes" },
  { value: "6", label: "Sábado" },
];

interface AutoMaintain {
  AutoRebootDay?: number;
  AutoRebootHour?: number;
  AutoRebootMinute?: number;
  AutoRebootEnable?: boolean;
}

export function SystemInfoTab({ device }: { device: Device }) {
  const [firmware, setFirmware] = useState<Awaited<ReturnType<typeof getFirmwareInfo>> | null>(null);
  const [maint, setMaint] = useState<AutoMaintain | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingMaint, setSavingMaint] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [fw, mt] = await Promise.all([
        getFirmwareInfo(device.id),
        getAutoMaintain(device.id).catch(() => ({ AutoMaintain: {} as AutoMaintain })),
      ]);
      setFirmware(fw);
      const am = (mt as any)?.AutoMaintain || (mt as any)?.table?.AutoMaintain || {};
      setMaint({
        AutoRebootEnable: !!am.AutoRebootEnable,
        AutoRebootDay: typeof am.AutoRebootDay === "number" ? am.AutoRebootDay : -1,
        AutoRebootHour: typeof am.AutoRebootHour === "number" ? am.AutoRebootHour : 2,
        AutoRebootMinute: typeof am.AutoRebootMinute === "number" ? am.AutoRebootMinute : 0,
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo sistema");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [device.id]);

  const saveMaintenance = async () => {
    if (!maint) return;
    setSavingMaint(true);
    try {
      await patchAutoMaintain(device.id, {
        "AutoMaintain.AutoRebootEnable": maint.AutoRebootEnable ? "true" : "false",
        "AutoMaintain.AutoRebootDay": String(maint.AutoRebootDay ?? -1),
        "AutoMaintain.AutoRebootHour": String(maint.AutoRebootHour ?? 2),
        "AutoMaintain.AutoRebootMinute": String(maint.AutoRebootMinute ?? 0),
      });
      toast.success("Programación de reinicio actualizada");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al guardar");
    } finally {
      setSavingMaint(false);
    }
  };

  if (loading) return <Spinner />;

  const sysInfo = firmware?.live?.system_info || {};
  const liveFirmware = firmware?.live?.firmware || firmware?.firmware_db || "-";
  const liveModel = firmware?.live?.model || firmware?.model || "-";
  const updateSerial = (sysInfo as any).updateSerial || "-";
  const processor = (sysInfo as any).processor || "-";
  const deviceType = (sysInfo as any).deviceType || "-";

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-base font-semibold">Sistema</h2>
        <p className="text-xs text-muted mt-0.5">Información del NVR y mantenimiento automático.</p>
      </div>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
        <h3 className="text-sm font-semibold mb-1">Información del dispositivo</h3>
        <table className="text-sm w-full">
          <tbody>
            {[
              ["Nombre", device.name],
              ["Host", `${device.host}:${device.http_port}`],
              ["Modelo", liveModel],
              ["Tipo (interno)", String(deviceType)],
              ["Serial", firmware?.live?.serial || device.serial || "-"],
              ["Update Serial", String(updateSerial)],
              ["Procesador", String(processor)],
              ["Firmware", liveFirmware],
              ["Fabricante", firmware?.live?.vendor || "-"],
              ["Hora del NVR", firmware?.live?.current_time || "-"],
              ["Canales", String(device.channels_count)],
              ["Online", device.is_online ? "Sí" : "No"],
              ["Habilitado", device.is_enabled ? "Sí" : "No"],
            ].map(([k, v]) => (
              <tr key={k} className="border-b border-line/40 last:border-b-0">
                <td className="text-muted pr-4 py-1.5 w-1/3">{k}</td>
                <td className="py-1.5 font-mono text-xs">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {firmware?.error && (
          <p className="text-xs text-yellow-300">No se pudo leer info live: {firmware.error}</p>
        )}
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Reinicio automático del NVR</h3>
        {maint ? (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!maint.AutoRebootEnable}
                onChange={(e) => setMaint({ ...maint, AutoRebootEnable: e.target.checked })}
              />
              <span>Habilitar reinicio programado</span>
            </label>
            <div className="grid grid-cols-3 gap-3 max-w-2xl">
              <Select
                label="Día"
                options={DAY_OPTS}
                value={String(maint.AutoRebootDay ?? -1)}
                onChange={(e) => setMaint({ ...maint, AutoRebootDay: Number(e.target.value) })}
                disabled={!maint.AutoRebootEnable}
              />
              <Input
                label="Hora (0-23)"
                type="number"
                min={0}
                max={23}
                value={String(maint.AutoRebootHour ?? 2)}
                onChange={(e) => setMaint({ ...maint, AutoRebootHour: Number(e.target.value) })}
                disabled={!maint.AutoRebootEnable}
              />
              <Input
                label="Minuto (0-59)"
                type="number"
                min={0}
                max={59}
                value={String(maint.AutoRebootMinute ?? 0)}
                onChange={(e) => setMaint({ ...maint, AutoRebootMinute: Number(e.target.value) })}
                disabled={!maint.AutoRebootEnable}
              />
            </div>
            <p className="text-xs text-muted">
              Día = "Todos los días" usa el valor -1 que entiende el firmware Dahua. La hora es local del NVR.
            </p>
            <div>
              <Button variant="primary" onClick={saveMaintenance} loading={savingMaint}>Guardar reinicio</Button>
            </div>
          </>
        ) : (
          <div className="text-xs text-muted">El DVR no expone AutoMaintain.</div>
        )}
      </section>
    </div>
  );
}
